import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, ArrowUpRight, ChevronDown } from "lucide-react";
import { MARKETING_SERVICES, IT_SERVICES } from "../data/mock";

const DROPDOWNS = [
  { to: "/services", label: "Services", items: MARKETING_SERVICES, base: "/services" },
  { to: "/it-services", label: "IT Services", items: IT_SERVICES, base: "/it-services" },
];

const SIMPLE_LINKS = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
];

const RIGHT_LINKS = [
  { to: "/portfolio", label: "Portfolio" },
  { to: "/team", label: "Team" },
  { to: "/contact", label: "Contact" },
];

const linkClass = ({ isActive }) =>
  `text-sm font-medium transition-colors duration-200 ${isActive ? "text-[#6C0B1B]" : "text-[#5C4B51] hover:text-[#6C0B1B]"}`;

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [mobileSub, setMobileSub] = useState(null);

  return (
    <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-[#FDFBF7]/80 border-b border-[#6C0B1B]/10">
      <nav className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center" aria-label="Levaze home">
          <img src="/logo-levaze.png" alt="Levaze" className="h-11 sm:h-12 w-auto object-contain" />
        </Link>

        <div className="hidden md:flex items-center gap-7">
          {SIMPLE_LINKS.map((l) => (
            <NavLink key={l.to} to={l.to} data-testid={`nav-link-${l.label.toLowerCase()}`} className={linkClass}>
              {l.label}
            </NavLink>
          ))}

          {DROPDOWNS.map((d) => (
            <div key={d.to} className="relative group">
              <NavLink to={d.to} data-testid={`nav-link-${d.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) => `${linkClass({ isActive })} inline-flex items-center gap-1`}>
                {d.label} <ChevronDown size={14} className="group-hover:rotate-180 transition-transform duration-200" />
              </NavLink>
              <div data-testid={`nav-dropdown-${d.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="invisible opacity-0 translate-y-2 group-hover:visible group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-200 absolute left-1/2 -translate-x-1/2 top-full pt-3">
                <div className="w-64 max-h-[70vh] overflow-y-auto rounded-2xl bg-white border border-[#6C0B1B]/10 shadow-[0_30px_60px_-20px_rgba(108,11,27,0.25)] p-2">
                  {d.items.map((s) => (
                    <Link key={s.slug} to={`${d.base}/${s.slug}`} data-testid={`nav-dropdown-item-${s.slug}`}
                      className="flex items-center gap-3 rounded-xl px-3 py-2.5 hover:bg-[#F3EFE9] transition-colors duration-150 group/item">
                      <span className="w-8 h-8 rounded-lg bg-[#6C0B1B]/8 flex items-center justify-center text-[#6C0B1B] shrink-0 group-hover/item:bg-[#6C0B1B] group-hover/item:text-white transition-colors duration-150">
                        <s.icon size={16} />
                      </span>
                      <span className="text-sm font-medium text-[#1A1014]">{s.title}</span>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          ))}

          {RIGHT_LINKS.map((l) => (
            <NavLink key={l.to} to={l.to} data-testid={`nav-link-${l.label.toLowerCase()}`} className={linkClass}>
              {l.label}
            </NavLink>
          ))}

          <Link to="/contact" data-testid="nav-cta-button"
            className="inline-flex items-center gap-1.5 rounded-full bg-[#6C0B1B] text-white text-sm font-medium px-5 py-2.5 hover:bg-[#8C1126] transition-colors duration-200">
            Get Started <ArrowUpRight size={15} />
          </Link>
        </div>

        <button data-testid="nav-mobile-toggle" className="md:hidden text-[#6C0B1B]" onClick={() => setOpen(!open)} aria-label="Toggle menu">
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </nav>

      {open && (
        <div data-testid="nav-mobile-menu" className="md:hidden bg-[#FDFBF7] border-b border-[#6C0B1B]/10 px-5 pb-5 flex flex-col gap-1 max-h-[80vh] overflow-y-auto">
          {SIMPLE_LINKS.map((l) => (
            <NavLink key={l.to} to={l.to} onClick={() => setOpen(false)} data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
              className="text-base font-medium text-[#1A1014] py-2.5">
              {l.label}
            </NavLink>
          ))}

          {DROPDOWNS.map((d) => (
            <div key={d.to} className="border-t border-[#6C0B1B]/8 pt-1">
              <button data-testid={`nav-mobile-toggle-${d.label.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => setMobileSub(mobileSub === d.label ? null : d.label)}
                className="w-full flex items-center justify-between text-base font-medium text-[#1A1014] py-2.5">
                {d.label}
                <ChevronDown size={16} className={`transition-transform duration-200 ${mobileSub === d.label ? "rotate-180" : ""}`} />
              </button>
              {mobileSub === d.label && (
                <div className="pl-3 pb-2 flex flex-col">
                  <NavLink to={d.to} onClick={() => setOpen(false)} className="text-sm font-semibold text-[#6C0B1B] py-2">
                    View all {d.label}
                  </NavLink>
                  {d.items.map((s) => (
                    <Link key={s.slug} to={`${d.base}/${s.slug}`} onClick={() => setOpen(false)} data-testid={`nav-mobile-item-${s.slug}`}
                      className="flex items-center gap-2.5 text-sm text-[#5C4B51] py-2">
                      <s.icon size={15} className="text-[#6C0B1B]" /> {s.title}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          ))}

          {RIGHT_LINKS.map((l) => (
            <NavLink key={l.to} to={l.to} onClick={() => setOpen(false)} data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
              className="text-base font-medium text-[#1A1014] py-2.5 border-t border-[#6C0B1B]/8">
              {l.label}
            </NavLink>
          ))}
        </div>
      )}
    </header>
  );
};
